import React, { useEffect, useState } from "react";
import StarIcon from "@mui/icons-material/Star";
import { teal } from "@mui/material/colors";
import { Button, Divider, Snackbar, Alert, Rating, LinearProgress } from "@mui/material";
import {
  Add,
  AddShoppingCart,
  Favorite,
  LocalShipping,
  Remove,
  Shield,
  Wallet,
  WorkspacePremium,
  Visibility,
  Storefront,
  RateReview,
} from "@mui/icons-material";
import SimilarProduct from "./SimilarProduct";
import ReviewCard from "../Review/ReviewCard";
import CreateReviewModal from "../Review/CreateReviewModal";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { fetchProductById, clearSelectedProduct } from "../../../State/customer/productSlice";
import { fetchReviewsByProductId, resetReviewStatus } from "../../../State/customer/reviewSlice";
import { useParams, useNavigate } from "react-router-dom";
import { AddProductToWishList } from "../../../State/customer/wishlistSlice";
import { addItemToCart } from "../../../State/customer/cartSlice";
import { Review } from "../../../types/reviewTypes";

const FALLBACK_IMAGE =
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTctl02UiSUqaJlEHt9du_AiYsMlg_Uq4t0eQ&s";

const ProductDetails = () => {
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState<string>("M");
  const [activeImage, setActiveImage] = useState(0);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { productId } = useParams();

  const { product, loading } = useAppSelector((state) => state.product);
  const { reviews, submitSuccess } = useAppSelector((state) => state.review);

  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (productId) {
      const numId = Number(productId);
      dispatch(fetchProductById(numId));
      dispatch(fetchReviewsByProductId(numId));
      setActiveImage(0);
      setQuantity(1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    return () => {
      dispatch(clearSelectedProduct());
    };
  }, [productId, dispatch]);

  useEffect(() => {
    if (submitSuccess) {
      setSnackbarMessage("Review submitted successfully!");
      setSnackbarOpen(true);
      dispatch(resetReviewStatus());
    }
  }, [submitSuccess, dispatch]);

  const handleActiveImage = (value: number) => () => {
    setActiveImage(value);
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleWishlist = () => {
    if (product?.id) {
      const jwt = localStorage.getItem("jwt");
      if (!jwt) {
        const guestWishlist = JSON.parse(localStorage.getItem("guest_wishlist") || "[]");
        const exists = guestWishlist.some((item: any) => item.id === product.id);
        if (!exists) {
          guestWishlist.push(product);
          localStorage.setItem("guest_wishlist", JSON.stringify(guestWishlist));
        }
        setSnackbarMessage("Added to wishlist successfully!");
        setSnackbarOpen(true);
        return;
      }

      dispatch(AddProductToWishList({ productId: product.id }))
        .unwrap()
        .then(() => {
          setSnackbarMessage("Added to wishlist successfully!");
          setSnackbarOpen(true);
        })
        .catch((error) => console.error("Wishlist error:", error));
    }
  };

  const handleAddToCart = () => {
    if (product?.id) {
      const jwt = localStorage.getItem("jwt");
      dispatch(
        addItemToCart({
          jwt: jwt,
          request: {
            productId: product.id,
            size: selectedSize,
            quantity: quantity,
            product: product,
          },
        })
      )
        .unwrap()
        .then(() => {
          setSnackbarMessage("Product added to cart successfully!");
          setSnackbarOpen(true);
        })
        .catch((error) => console.error("Add to cart error:", error));
    }
  };

  const handleOpenReviewModal = () => {
    const jwt = localStorage.getItem("jwt");
    if (!jwt) {
      setSnackbarMessage("Please log in to leave a review.");
      setSnackbarOpen(true);
      return;
    }
    setIsReviewModalOpen(true);
  };

  // Calculate Rating Statistics dynamically with explicit types
  const totalReviews = reviews?.length || 0;
  const averageRating =
    totalReviews > 0
      ? (reviews.reduce((acc: number, r: Review) => acc + r.rating, 0) / totalReviews).toFixed(1)
      : "0.0";

  const getRatingCount = (star: number) =>
    reviews ? reviews.filter((r: Review) => Math.round(r.rating) === star).length : 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh] text-teal-600 font-medium">
        Loading product details...
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex justify-center items-center min-h-[50vh] text-gray-500 font-medium">
        Product not found.
      </div>
    );
  }

  const images = product?.images?.length ? product.images : [FALLBACK_IMAGE];

  const getFormattedDescriptionBullets = (description?: string) => {
    if (!description) return [];
    return description
      .split(/(?<=[.!?])\s+|\n+/)
      .map((bullet) => bullet.trim())
      .filter((bullet) => bullet.length > 0);
  };

  const descriptionBullets = getFormattedDescriptionBullets(product?.description);
  const sellerId = product?.seller?.id;
  const sellerName =
    product?.seller?.sellerName || product?.seller?.businessDetails?.businessName || "Verified Vendor";

  return (
    <div className="px-4 sm:px-8 lg:px-20 py-6 lg:py-10 max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Images */}
        <section className="flex flex-col-reverse lg:flex-row gap-4 lg:gap-5">
          <div className="w-full lg:w-[15%] flex flex-row lg:flex-col gap-3 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0 scrollbar-none">
            {images.map((item, index) => (
              <img
                key={index}
                onClick={handleActiveImage(index)}
                className={`w-[60px] h-[60px] sm:w-[75px] sm:h-[75px] lg:w-full lg:h-20 object-cover flex-shrink-0 cursor-pointer rounded-md border-2 transition-all ${activeImage === index
                    ? "border-teal-600 shadow-sm opacity-100"
                    : "border-gray-200 opacity-70 hover:opacity-100 hover:border-teal-300"
                  }`}
                src={item}
                alt={`Thumbnail ${index + 1}`}
              />
            ))}
          </div>

          <div className="w-full lg:w-[85%]">
            <img
              className="w-full rounded-lg object-cover max-h-[350px] sm:max-h-[480px] lg:max-h-[600px] shadow-sm border border-gray-100"
              src={images[activeImage] || images[0]}
              alt={product?.title || "Product image"}
            />
          </div>
        </section>

        {/* Details */}
        <section className="flex flex-col justify-between">
          <div>
            <h1 className="font-bold text-xl sm:text-2xl lg:text-3xl text-gray-800 leading-snug">
              {product?.title || "Product Title"}
            </h1>

            {/* Description Bullets */}
            <div className="mt-3">
              <ul className="list-disc pl-5 space-y-1.5 text-gray-600 text-sm sm:text-base leading-relaxed">
                {descriptionBullets.map((bullet, index) => (
                  <li key={index}>{bullet}</li>
                ))}
              </ul>
            </div>

            {/* Dynamic Rating Summary Pill */}
            <div className="flex items-center gap-3 py-1 border border-teal-100 bg-teal-50/50 rounded-md w-fit px-3 mt-4 text-xs sm:text-sm font-medium text-gray-700">
              <div className="flex gap-1 items-center">
                <span>{averageRating}</span>
                <StarIcon sx={{ color: teal[600], fontSize: "18px" }} />
              </div>
              <Divider orientation="vertical" flexItem sx={{ borderColor: teal[100] }} />
              <span className="text-teal-900">{totalReviews} Ratings</span>
            </div>

            <div className="mt-5">
              <div className="price flex items-baseline flex-wrap gap-3 text-xl sm:text-2xl lg:text-3xl">
                <span className="font-sans font-bold text-gray-900">
                  Ksh {product?.sellingPrice?.toLocaleString()}
                </span>
                {product?.mrpPrice > product?.sellingPrice && (
                  <>
                    <span className="line-through text-gray-400 text-lg sm:text-xl">
                      Ksh {product?.mrpPrice?.toLocaleString()}
                    </span>
                    <span className="text-teal-600 font-semibold text-base sm:text-lg">
                      {product?.discountPercent}% OFF
                    </span>
                  </>
                )}
              </div>
              <p className="text-xs sm:text-sm mt-1 text-gray-500">Inclusive of all taxes. Free shipping</p>

              {/* Vendor Info + View Shop Button */}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 bg-gray-50 border border-gray-200 p-3 rounded-lg max-w-md">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Storefront fontSize="small" className="text-teal-600 flex-shrink-0" />
                  <span>
                    Sold by: <strong className="font-semibold text-gray-900">{sellerName}</strong>
                  </span>
                </div>

                {sellerId && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Visibility fontSize="small" />}
                    onClick={() => navigate(`/seller-store/${sellerId}`)}
                    sx={{
                      color: teal[700],
                      borderColor: teal[600],
                      textTransform: "none",
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      borderRadius: "6px",
                      px: 1.5,
                      py: 0.5,
                      "&:hover": {
                        bgcolor: teal[50],
                        borderColor: teal[700],
                      },
                    }}
                  >
                    View Shop
                  </Button>
                )}
              </div>
            </div>

            {/* Quantity */}
            <div className="mt-6 space-y-2">
              <h2 className="font-semibold text-xs sm:text-sm text-gray-700 tracking-wide uppercase">
                Quantity
              </h2>
              <div className="flex items-center gap-1 border border-teal-200 rounded-md w-fit bg-white">
                <Button
                  size="small"
                  disabled={quantity === 1}
                  onClick={() => setQuantity(quantity - 1)}
                  sx={{
                    minWidth: "40px",
                    color: teal[700],
                    "&:hover": { bgcolor: teal[50] },
                  }}
                >
                  <Remove fontSize="small" />
                </Button>
                <span className="font-semibold text-base px-3 text-gray-800">{quantity}</span>
                <Button
                  size="small"
                  onClick={() => setQuantity(quantity + 1)}
                  sx={{
                    minWidth: "40px",
                    color: teal[700],
                    "&:hover": { bgcolor: teal[50] },
                  }}
                >
                  <Add fontSize="small" />
                </Button>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm text-gray-600">
              <div className="flex items-center gap-3">
                <Shield sx={{ color: teal[600], fontSize: 20 }} />
                <span>Authentic & Quality Assured</span>
              </div>
              <div className="flex items-center gap-3">
                <WorkspacePremium sx={{ color: teal[600], fontSize: 20 }} />
                <span>100% Money Back Guarantee</span>
              </div>
              <div className="flex items-center gap-3">
                <LocalShipping sx={{ color: teal[600], fontSize: 20 }} />
                <span>Free Shipping & Easy Returns</span>
              </div>
              <div className="flex items-center gap-3">
                <Wallet sx={{ color: teal[600], fontSize: 20 }} />
                <span>Pay on Delivery Available</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
            <Button
              onClick={handleAddToCart}
              fullWidth
              variant="contained"
              startIcon={<AddShoppingCart />}
              sx={{
                py: { xs: "12px", sm: "14px" },
                bgcolor: teal[600],
                "&:hover": { bgcolor: teal[700] },
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                boxShadow: "0 2px 4px rgba(13, 148, 136, 0.2)",
              }}
            >
              Add To Cart
            </Button>

            <Button
              onClick={handleWishlist}
              fullWidth
              variant="outlined"
              startIcon={<Favorite />}
              sx={{
                py: { xs: "12px", sm: "14px" },
                color: teal[700],
                borderColor: teal[600],
                "&:hover": {
                  borderColor: teal[700],
                  bgcolor: teal[50],
                },
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              Add To Wishlist
            </Button>
          </div>
        </section>
      </div>

      {/* Ratings & Reviews Breakdown Section */}
      <section className="mt-12 sm:mt-16 border-t border-gray-100 pt-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Ratings & Reviews</h2>
            <p className="text-sm text-gray-500">Read product reviews from verified customers</p>
          </div>
          <Button
            variant="outlined"
            startIcon={<RateReview />}
            onClick={handleOpenReviewModal}
            sx={{
              borderColor: teal[600],
              color: teal[700],
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: teal[50], borderColor: teal[700] },
            }}
          >
            Write Review
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-8 bg-gray-50 p-6 rounded-xl border border-gray-100">
          {/* Average Rating Overall */}
          <div className="md:col-span-4 flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-gray-200 pb-6 md:pb-0">
            <span className="text-5xl font-extrabold text-gray-900">{averageRating}</span>
            <Rating value={Number(averageRating)} precision={0.1} readOnly size="large" sx={{ my: 1 }} />
            <span className="text-sm text-gray-500 font-medium">{totalReviews} Ratings & Reviews</span>
          </div>

          {/* Rating Distribution Breakdown */}
          <div className="md:col-span-8 flex flex-col justify-center space-y-2">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = getRatingCount(star);
              const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
              return (
                <div key={star} className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-gray-600 w-12">{star} ★</span>
                  <div className="flex-1">
                    <LinearProgress
                      variant="determinate"
                      value={percentage}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: "#e5e7eb",
                        "& .MuiLinearProgress-bar": { bgcolor: teal[600] },
                      }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 w-8 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Customer Reviews List */}
        <div className="space-y-4">
          {reviews && reviews.length > 0 ? (
            reviews.map((review: Review) => (
              <ReviewCard key={review.id} review={review} currentUserId={user?.id} />
            ))
          ) : (
            <div className="text-center py-8 text-gray-500 text-sm">
              No reviews yet for this product. Be the first to leave feedback!
            </div>
          )}
        </div>
      </section>

      {/* Write Review Modal */}
      {product?.id && (
        <CreateReviewModal
          open={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          productId={product.id}
        />
      )}

      {/* Similar Products */}
      <div className="mt-12 sm:mt-16 border-t border-gray-100 pt-8">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Similar Products</h2>
        <div className="pt-4">
          <SimilarProduct />
        </div>
      </div>

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success" variant="filled" sx={{ width: "100%", bgcolor: teal[700], color: "#fff" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ProductDetails;